<template>
  <div
    v-on-clickaway="hideMenu"
    class="DropdownMenu__wrapper"
  >
    <component
      :is="toggleComponent"
      v-bind="toggleProps"
      :aria-controls="menuId$"
      :aria-expanded="isExpanded"
      data-test-id="DropdownMenu__ToggleButton"
      :isDisabled="isDisabled"
      @click="toggleMenu"
    />

    <div
      :id="menuId$"
      :aria-hidden="!isExpanded"
      :class="containerClass"
      data-test-id="DropdownMenu__container"
    >
      <header
        v-if="$scopedSlots.header"
        class="DropdownMenu__header"
      >
        <slot
          :isExpanded="isExpanded"
          :menuId="menuId$"
          name="header"
        />
      </header>

      <menu class="DropdownMenu__menu">
        <li
          v-for="(item, i) in items"
          :key="i"
          :class="itemClass(item)"
          data-test-id="DropdownMenu__item"
        >
          <Checkbox
            v-if="selectable"
            ref="checkboxes"
            v-bind="item.props"
            data-test-id="DropdownMenu__Checkbox"
            :isDisabled="isDisabled"
            @input="(isChecked) => handleCheckboxChange(isChecked, item, i)"
          />

          <slot
            v-else-if="$scopedSlots.item"
            v-bind="{
              ...item,
              isExpanded,
            }"
            name="item"
          />
        </li>
      </menu>
    </div>
  </div>
</template>

<script>
import {
  Button,
  Checkbox,
  IconButton,
} from '@jumpcloud/ui-components';
import { mixin as clickaway } from 'vue-clickaway';

export default {
  name: 'DropdownMenu',

  components: {
    Checkbox,
  },

  mixins: [clickaway],

  props: {
    align: {
      type: String,
      default: 'left',
      validator(value) {
        return ['left', 'right'].includes(value);
      },
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    items: {
      type: Array,
      required: true,
    },

    menuId: {
      type: String,
      default: '',
    },

    selectable: {
      type: Boolean,
      default: false,
    },

    toggleProps: {
      type: Object,
      default: () => ({
        text: 'Options',
      }),
    },
  },

  data: () => ({
    isExpanded: false,
    selectedItems: [],
  }),

  computed: {
    containerClass() {
      return {
        DropdownMenu__container: true,
        [`DropdownMenu__container--align-${this.align}`]: true,
        'DropdownMenu__container--expanded': this.isExpanded,
      };
    },

    menuId$() {
      // eslint-disable-next-line no-underscore-dangle
      return this.menuId || `menu-${this._uid}`;
    },

    tabindex() {
      return this.isExpanded ? 0 : -1;
    },

    toggleComponent() {
      return this.toggleProps.icon ? IconButton : Button;
    },
  },

  methods: {
    handleCheckboxChange(isChecked, item, i) {
      this.$emit('checkboxChange', isChecked, item, i);
    },

    hideMenu() {
      this.toggleMenu(false);
    },

    itemClass(item) {
      return [
        'DropdownMenu__menuItem',
        item.props?.class || '',
      ];
    },

    // Sort of hacky workaround to patch missing tabindex passthrough prop on Checkbox
    setCheckboxesTabIndex() {
      const checkboxesRef = this.$refs?.checkboxes;

      if (checkboxesRef?.length) {
        checkboxesRef.forEach(checkbox => {
          const checkboxRef = checkbox.$refs?.checkbox;

          if (checkboxRef?.setAttribute) {
            checkboxRef.setAttribute('tabindex', this.tabindex);
          }
        });
      }
    },

    toggleMenu(isExpanded) {
      this.isExpanded = typeof isExpanded === 'boolean'
        ? isExpanded
        : !this.isExpanded;

      if (this.selectable) {
        this.setCheckboxesTabIndex();
      }

      this.$emit('toggleMenu', this.isExpanded);
    },
  },
};
</script>

<style scoped>
.DropdownMenu__wrapper {
  position: relative;
}

.DropdownMenu__header {
  padding: var(--jc-spacer-small) var(--jc-spacer);
}

.DropdownMenu__container {
  background: var(--jc-background-light);
  border: var(--jc-border);
  border-radius: var(--jc-border-radius-small);
  box-shadow: var(--jc-box-shadow);
  list-style: none;
  opacity: 0;
  padding: var(--jc-spacer-small) 0;
  pointer-events: none;
  position: absolute;
  top: calc(100% + var(--jc-spacer-small));
  transform: translateY(var(--jc-spacer-negative-x-small));
  transition: opacity var(--jc-transition-duration), transform var(--jc-transition-duration);
  z-index: 3;
}

.DropdownMenu__container--expanded {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

.DropdownMenu__container--align-left {
  left: var(--jc-spacer-negative-small);
}

.DropdownMenu__container--align-right {
  right: var(--jc-spacer-negative-small);
}

.DropdownMenu__menu {
  list-style: none;
  margin: var(--jc-spacer-small) 0 0;
  padding: 0;
}

.DropdownMenu__menuItem {
  padding: var(--jc-spacer-small) var(--jc-spacer);
  text-align: left;
  white-space: nowrap;
}

.DropdownMenu__itemIconLeft {
  margin-right: var(--jc-spacer-small);
}

.DropdownMenu__itemIconRight {
  margin-left: var(--jc-spacer-small);
}
</style>
